import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Breadcrumb from '../../components/layout/Breadcrumb'
import { useAuth } from '../../providers/AuthProvider'
import { useExpertReviews } from '../../hooks/useExpertReviews'
import { supabase } from '../../supabaseClient'
import {
  ConfidenceLevel,
  Experiment,
  FrameworkFit,
  KnowledgeUnit,
  LikertScore,
  PASS_META,
  ReviewResponses,
} from '../../types'

/**
 * Two-step expert review screen.
 *   Step 1: atom quality (three Likert scales: Atomicity, Self-containment,
 *           Source fidelity) + validity override + flag-for-debrief.
 *   Step 2: classification (expert picks type blind — LLM's proposed type
 *           is never shown here). Submit = locks the per-KU review row.
 *
 * Rationale is required on edge cases:
 *   - "Not a valid KU" (sme_valid=no)  → atom_notes required
 *   - type === 'unclear'                → classification_notes required
 *   - confidence === 'L' (low)          → classification_notes required
 *   - framework_fit === 'doesnt_fit'    → classification_notes required
 *
 * Expert is blind to AI's classification by design.
 */

const LIKERT_ANCHORS: Record<'atomicity' | 'self_containment' | 'source_fidelity', { low: string; high: string }> = {
  atomicity: { low: 'multiple elements', high: 'single element' },
  self_containment: { low: 'needs context', high: 'stands alone' },
  source_fidelity: { low: 'LLM-added', high: 'directly from source' },
}

export default function ExperimentReviewPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user, profile } = useAuth()
  const {
    reviews,
    loading: reviewsLoading,
    load: loadReviews,
    saveStep1,
    saveStep2,
    lockReview,
    getReviewForKU,
  } = useExpertReviews(id)

  const [experiment, setExperiment] = useState<Experiment | null>(null)
  const [kus, setKUs] = useState<KnowledgeUnit[]>([])
  const [kusLoading, setKUsLoading] = useState(true)
  const [selectedIdx, setSelectedIdx] = useState(0)
  const [passFilter, setPassFilter] = useState<string>('all')
  const [reviewFilter, setReviewFilter] = useState<string>('unreviewed') // unreviewed | reviewed | all
  const [saveError, setSaveError] = useState<string | null>(null)

  // Experiment fetched by id (owner filter doesn't work for expert role)
  useEffect(() => {
    if (!id) return
    ;(async () => {
      const { data, error } = await supabase
        .from('experiments')
        .select('*, experiment_standards(standard_id, grade)')
        .eq('id', id)
        .single()
      if (error || !data) {
        setExperiment(null)
        return
      }
      setExperiment(data as Experiment)
    })()
  }, [id])

  const loadKUs = useCallback(async () => {
    if (!id) return
    setKUsLoading(true)
    const { data } = await supabase
      .from('knowledge_units')
      .select('*')
      .eq('experiment_id', id)
      .order('standard_id')
      .order('pass_id')
    setKUs((data ?? []) as KnowledgeUnit[])
    setKUsLoading(false)
  }, [id])

  useEffect(() => {
    loadKUs()
    loadReviews()
  }, [loadKUs, loadReviews])

  // Filter KUs by pass and review status
  const filteredKUs = useMemo(() => {
    let list = kus
    if (passFilter !== 'all') list = list.filter((ku) => ku.pass_id === passFilter)
    const reviewedLocked = new Set(reviews.filter((r) => r.reviewer_id === user?.id && r.locked_at).map((r) => r.ku_id))
    if (reviewFilter === 'reviewed') list = list.filter((ku) => reviewedLocked.has(ku.id))
    else if (reviewFilter === 'unreviewed') list = list.filter((ku) => !reviewedLocked.has(ku.id))
    return list
  }, [kus, passFilter, reviewFilter, reviews, user?.id])

  const currentKU = filteredKUs[selectedIdx]
  const currentReview = currentKU ? getReviewForKU(currentKU.id, user?.id) : undefined
  const responses: ReviewResponses = currentReview?.responses ?? {}
  const isLocked = !!currentReview?.locked_at
  const reviewedCount = useMemo(
    () => reviews.filter((r) => r.reviewer_id === user?.id && r.locked_at).length,
    [reviews, user?.id],
  )
  const passes = useMemo(() => Array.from(new Set(kus.map((ku) => ku.pass_id))), [kus])

  // ── Step 1 handlers ────────────────────────────────────────────
  const handleStep1Change = async (patch: Partial<ReviewResponses>) => {
    if (!currentKU || !user || isLocked) return
    setSaveError(null)
    try {
      await saveStep1(currentKU.id, user.id, patch)
    } catch (e) {
      setSaveError('Save failed — check your connection and try again.')
    }
  }

  const handleStep2Change = async (patch: Partial<ReviewResponses>) => {
    if (!currentKU || !user || isLocked) return
    setSaveError(null)
    try {
      await saveStep2(currentKU.id, user.id, patch)
    } catch (e) {
      setSaveError('Save failed — check your connection and try again.')
    }
  }

  // ── Three-layer architecture: Review UI branches on audit_mode ─────
  // platform_validation → Step 1 only (atom quality; platform-side reviewer)
  // theory_classification → Step 2 only (taxonomy fit; theorist)
  // full → both (legacy flexibility)
  const auditMode = experiment?.audit_mode ?? 'full'
  const showStep1 = auditMode === 'platform_validation' || auditMode === 'full'
  const showStep2 = auditMode === 'theory_classification' || auditMode === 'full'

  // ── Submit (lock) ──────────────────────────────────────────────
  const step1Complete = !showStep1 // not required when hidden
    ? true
    : responses.valid === 'no'
    ? !!(responses.atom_notes && responses.atom_notes.trim()) // invalid → notes required
    : !!(responses.atomicity_score && responses.self_containment_score && responses.source_fidelity_score)

  // Step 2 is visible when audit mode includes it AND (in 'full' mode)
  // the atom quality hasn't marked it invalid.
  const step2Visible =
    showStep2 && (auditMode === 'theory_classification' || (step1Complete && responses.valid !== 'no'))

  const needsClassificationRationale =
    responses.expert_type === 'unclear' || responses.confidence === 'L' || responses.framework_fit === 'doesnt_fit'

  const step2Complete = !showStep2 // not required when hidden
    ? true
    : responses.valid === 'no' && auditMode === 'full'
    ? true // invalid → skip step 2 entirely in full mode
    : !!(responses.expert_type && responses.confidence && responses.framework_fit) &&
      (!needsClassificationRationale || !!(responses.classification_notes && responses.classification_notes.trim()))

  const canSubmit = step1Complete && step2Complete && !isLocked

  const handleSubmit = async () => {
    if (!currentKU || !user || !canSubmit) return
    await lockReview(currentKU.id, user.id)
    // Auto-advance to next unreviewed after lock
    if (selectedIdx < filteredKUs.length - 1) {
      setSelectedIdx(selectedIdx + 1)
    }
  }

  // ── Guards ─────────────────────────────────────────────────────
  if (!experiment) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Breadcrumb
          label={profile?.role === 'expert' ? 'My Reviews' : 'Experiments'}
          onBack={() => navigate(profile?.role === 'expert' ? '/my-reviews' : '/datasets')}
        />
        <Box sx={{ p: 3 }}>
          <Alert severity="error">Experiment not found or not accessible.</Alert>
        </Box>
      </Box>
    )
  }

  if (kusLoading || reviewsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  const taxonomyTypes = experiment.taxonomy?.types ?? [
    'fact',
    'concept',
    'procedure',
    'process',
    'principle',
    'unclear',
  ]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.default' }}>
      <Breadcrumb
        label={profile?.role === 'expert' ? 'My Reviews' : 'Experiments'}
        onBack={() => navigate(profile?.role === 'expert' ? '/my-reviews' : '/datasets')}
      />

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* ── Left: filters + KU queue ── */}
        <Box
          sx={{
            width: 300,
            borderRight: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'primary.main', mb: 0.25 }}>
              {experiment.name}
            </Typography>
            <Typography sx={{ fontSize: 10.5, color: 'text.secondary', letterSpacing: '0.05em', mb: 1 }}>
              EXPERT REVIEW · {reviewedCount} / {kus.length} complete
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Select
                value={passFilter}
                onChange={(e) => {
                  setPassFilter(e.target.value)
                  setSelectedIdx(0)
                }}
                size="small"
                sx={{ fontSize: 11, flex: 1 }}
              >
                <MenuItem value="all" sx={{ fontSize: 11 }}>
                  All passes
                </MenuItem>
                {passes.map((p) => (
                  <MenuItem key={p} value={p} sx={{ fontSize: 11 }}>
                    {PASS_META.find((pm) => pm.id === p)?.label || p}
                  </MenuItem>
                ))}
              </Select>
              <Select
                value={reviewFilter}
                onChange={(e) => {
                  setReviewFilter(e.target.value)
                  setSelectedIdx(0)
                }}
                size="small"
                sx={{ fontSize: 11, flex: 1 }}
              >
                <MenuItem value="unreviewed" sx={{ fontSize: 11 }}>
                  To review
                </MenuItem>
                <MenuItem value="reviewed" sx={{ fontSize: 11 }}>
                  Done
                </MenuItem>
                <MenuItem value="all" sx={{ fontSize: 11 }}>
                  All
                </MenuItem>
              </Select>
            </Box>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {filteredKUs.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>No KUs match.</Typography>
              </Box>
            ) : (
              filteredKUs.map((ku, idx) => {
                const r = getReviewForKU(ku.id, user?.id)
                const locked = !!r?.locked_at
                const flagged = r?.responses?.flag_for_debrief
                const isSelected = idx === selectedIdx
                return (
                  <Box
                    key={ku.id}
                    onClick={() => setSelectedIdx(idx)}
                    sx={{
                      px: 1.5,
                      py: 1,
                      cursor: 'pointer',
                      bgcolor: isSelected ? 'primary.main' : 'background.paper',
                      color: isSelected ? '#fff' : 'text.primary',
                      borderBottom: '1px solid',
                      borderColor: isSelected ? 'primary.dark' : 'divider',
                      '&:hover': { bgcolor: isSelected ? 'primary.main' : 'grey.50' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: locked ? '#16a34a' : '#e2e8f0',
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 700,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                        }}
                      >
                        {ku.name}
                      </Typography>
                      {flagged && (
                        <Typography sx={{ fontSize: 11, color: isSelected ? '#fde68a' : '#d97706' }}>🚩</Typography>
                      )}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 10,
                        color: isSelected ? 'rgba(255,255,255,0.75)' : 'text.secondary',
                        pl: 1.75,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {ku.standard_id} · {PASS_META.find((pm) => pm.id === ku.pass_id)?.label || ku.pass_id}
                    </Typography>
                  </Box>
                )
              })
            )}
          </Box>
        </Box>

        {/* ── Right: KU detail + two-step review ── */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {!currentKU ? (
            <Box sx={{ textAlign: 'center', pt: 8 }}>
              <Typography color="text.secondary">Select a KU to review, or all done for this filter.</Typography>
            </Box>
          ) : (
            <Box sx={{ p: 3, maxWidth: 820 }}>
              {saveError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
                  {saveError}
                </Alert>
              )}

              {/* KU header — theory-neutral, no proposed_type shown */}
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', letterSpacing: '0.05em' }}>
                  {currentKU.standard_id} · {currentKU.pass_id}
                </Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 800, color: 'primary.main', mt: 0.5 }}>
                  {currentKU.name}
                </Typography>
                <Typography sx={{ fontSize: 14, color: 'text.primary', mt: 0.75, lineHeight: 1.6 }}>
                  {currentKU.description}
                </Typography>
                {isLocked && (
                  <Chip
                    label={`✓ Submitted ${new Date(currentReview!.locked_at!).toLocaleString()}`}
                    size="small"
                    sx={{ mt: 1, bgcolor: '#dcfce7', color: '#166534', fontWeight: 700, fontSize: 11 }}
                  />
                )}
              </Box>

              {/* ── STEP 1 · Atom quality (platform validation) ── */}
              {showStep1 && (
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: step1Complete ? '#86efac' : 'divider',
                    borderRadius: 2,
                    p: 2,
                    mb: 2,
                    bgcolor: 'background.paper',
                    opacity: isLocked ? 0.7 : 1,
                    pointerEvents: isLocked ? 'none' : 'auto',
                  }}
                >
                  <Typography
                    sx={{ fontSize: 12, fontWeight: 800, color: 'primary.main', letterSpacing: '0.05em', mb: 1.5 }}
                  >
                    STEP 1 · ATOM QUALITY (theory-neutral)
                  </Typography>

                  <LikertRow
                    label="Atomicity"
                    value={responses.atomicity_score}
                    anchors={LIKERT_ANCHORS.atomicity}
                    onChange={(v) => handleStep1Change({ atomicity_score: v })}
                    disabled={isLocked || responses.valid === 'no'}
                  />
                  <LikertRow
                    label="Self-containment"
                    value={responses.self_containment_score}
                    anchors={LIKERT_ANCHORS.self_containment}
                    onChange={(v) => handleStep1Change({ self_containment_score: v })}
                    disabled={isLocked || responses.valid === 'no'}
                  />
                  <LikertRow
                    label="Source fidelity"
                    value={responses.source_fidelity_score}
                    anchors={LIKERT_ANCHORS.source_fidelity}
                    onChange={(v) => handleStep1Change({ source_fidelity_score: v })}
                    disabled={isLocked || responses.valid === 'no'}
                  />

                  {/* Composite */}
                  {responses.atomicity_score && responses.self_containment_score && responses.source_fidelity_score && (
                    <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 1 }}>
                      Composite:{' '}
                      <strong>
                        {(
                          ((responses.atomicity_score || 0) +
                            (responses.self_containment_score || 0) +
                            (responses.source_fidelity_score || 0)) /
                          3
                        ).toFixed(1)}{' '}
                        / 5.0
                      </strong>
                    </Typography>
                  )}

                  {/* Not-a-valid-KU checkbox */}
                  <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <input
                      type="checkbox"
                      id="not-valid"
                      checked={responses.valid === 'no'}
                      disabled={isLocked}
                      onChange={(e) =>
                        handleStep1Change({
                          valid: e.target.checked ? 'no' : undefined,
                          // Clear scores when flagging as invalid
                          ...(e.target.checked
                            ? {
                                atomicity_score: undefined,
                                self_containment_score: undefined,
                                source_fidelity_score: undefined,
                              }
                            : {}),
                        } as Partial<ReviewResponses>)
                      }
                    />
                    <label htmlFor="not-valid" style={{ fontSize: 12, color: '#475569' }}>
                      Not a valid KU — skip ratings
                    </label>
                  </Box>

                  {/* Atom notes */}
                  <TextField
                    value={responses.atom_notes ?? ''}
                    onChange={(e) => handleStep1Change({ atom_notes: e.target.value })}
                    disabled={isLocked}
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder={
                      responses.valid === 'no'
                        ? 'Required: why is this not a valid KU?'
                        : 'Notes (optional) — especially for low scores'
                    }
                    sx={{ mt: 1.5, '& textarea': { fontSize: 12 } }}
                  />

                  {/* Flag for debrief */}
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <input
                      type="checkbox"
                      id="flag-debrief"
                      checked={!!responses.flag_for_debrief}
                      disabled={isLocked}
                      onChange={(e) => handleStep1Change({ flag_for_debrief: e.target.checked })}
                    />
                    <label htmlFor="flag-debrief" style={{ fontSize: 12, color: '#475569' }}>
                      🚩 Flag for debrief — worth discussing later
                    </label>
                  </Box>
                </Box>
              )}

              {/* ── STEP 2 · Classification ── */}
              {step2Visible ? (
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: step2Complete ? '#86efac' : 'divider',
                    borderRadius: 2,
                    p: 2,
                    mb: 2,
                    bgcolor: 'background.paper',
                    opacity: isLocked ? 0.7 : 1,
                    pointerEvents: isLocked ? 'none' : 'auto',
                  }}
                >
                  <Typography
                    sx={{ fontSize: 12, fontWeight: 800, color: 'primary.main', letterSpacing: '0.05em', mb: 1.5 }}
                  >
                    STEP 2 · CLASSIFICATION (theory-bound · expert blind to LLM)
                  </Typography>

                  <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>Type</Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
                    {taxonomyTypes.map((t) => (
                      <Chip
                        key={t}
                        label={t}
                        onClick={() => !isLocked && handleStep2Change({ expert_type: t })}
                        sx={{
                          fontSize: 12,
                          fontWeight: 700,
                          bgcolor: responses.expert_type === t ? 'primary.main' : 'grey.100',
                          color: responses.expert_type === t ? '#fff' : 'text.primary',
                          cursor: isLocked ? 'default' : 'pointer',
                          '&:hover': { bgcolor: responses.expert_type === t ? 'primary.main' : 'grey.200' },
                        }}
                      />
                    ))}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                    <Box>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5 }}>Confidence</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {(['H', 'M', 'L'] as ConfidenceLevel[]).map((c) => (
                          <Chip
                            key={c}
                            label={c}
                            size="small"
                            onClick={() => !isLocked && handleStep2Change({ confidence: c })}
                            sx={{
                              fontSize: 11,
                              fontWeight: 700,
                              bgcolor: responses.confidence === c ? 'primary.main' : 'grey.100',
                              color: responses.confidence === c ? '#fff' : 'text.primary',
                              cursor: isLocked ? 'default' : 'pointer',
                            }}
                          />
                        ))}
                      </Box>
                    </Box>

                    <Box>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5 }}>Framework fit</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(
                          [
                            [
                              'clearly_fits',
                              'clearly fits',
                              'The KU maps unambiguously to one category in the taxonomy. Minimal interpretation required.',
                            ],
                            [
                              'forced_fit',
                              'forced',
                              'The KU can be assigned a category, but only by stretching or interpreting the taxonomy definition. Boundary case.',
                            ],
                            [
                              'doesnt_fit',
                              "doesn't fit",
                              'The KU genuinely falls outside the taxonomy — no category captures it. A true framework gap.',
                            ],
                          ] as [FrameworkFit, string, string][]
                        ).map(([v, label, tooltip]) => (
                          <Tooltip key={v} title={tooltip} arrow placement="top">
                            <Chip
                              label={label}
                              size="small"
                              onClick={() =>
                                !isLocked &&
                                handleStep2Change({
                                  // Clicking the already-selected chip clears it.
                                  framework_fit: responses.framework_fit === v ? undefined : v,
                                })
                              }
                              sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                bgcolor: responses.framework_fit === v ? 'primary.main' : 'grey.100',
                                color: responses.framework_fit === v ? '#fff' : 'text.primary',
                                cursor: isLocked ? 'default' : 'pointer',
                              }}
                            />
                          </Tooltip>
                        ))}
                        {responses.framework_fit && !isLocked && (
                          <Chip
                            label="✕ clear"
                            size="small"
                            title="Undo — clear the framework-fit selection"
                            onClick={() => handleStep2Change({ framework_fit: undefined })}
                            sx={{
                              fontSize: 11,
                              fontWeight: 700,
                              bgcolor: 'transparent',
                              color: 'text.secondary',
                              border: '1px dashed',
                              borderColor: 'divider',
                              cursor: 'pointer',
                              '&:hover': { bgcolor: 'grey.100' },
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>

                  <TextField
                    value={responses.classification_notes ?? ''}
                    onChange={(e) => handleStep2Change({ classification_notes: e.target.value })}
                    disabled={isLocked}
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder={
                      needsClassificationRationale
                        ? 'Required: rationale for this decision'
                        : 'Notes (optional) — what feature determined your classification?'
                    }
                    sx={{ '& textarea': { fontSize: 12 } }}
                  />
                </Box>
              ) : responses.valid === 'no' ? (
                <Box
                  sx={{
                    p: 1.75,
                    bgcolor: '#fff7ed',
                    border: '1px dashed',
                    borderColor: '#fed7aa',
                    borderRadius: 2,
                    mb: 2,
                  }}
                >
                  <Typography sx={{ fontSize: 12, color: '#9a3412' }}>
                    Marked as not a valid KU — classification step skipped.
                  </Typography>
                </Box>
              ) : (
                <Typography sx={{ fontSize: 11, color: 'text.disabled', mb: 2, textAlign: 'center' }}>
                  Complete Step 1 to unlock Step 2
                </Typography>
              )}

              {/* ── Submit ── */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    onClick={() => setSelectedIdx(Math.max(0, selectedIdx - 1))}
                    disabled={selectedIdx === 0}
                    size="small"
                    sx={{ fontSize: 12 }}
                  >
                    ← Prev
                  </Button>
                  <Button
                    onClick={() => setSelectedIdx(Math.min(filteredKUs.length - 1, selectedIdx + 1))}
                    disabled={selectedIdx >= filteredKUs.length - 1}
                    size="small"
                    sx={{ fontSize: 12 }}
                  >
                    Next →
                  </Button>
                </Box>
                {!isLocked && (
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    sx={{ fontSize: 12, fontWeight: 700, bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}
                  >
                    Submit &amp; Lock ✓
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

// ── Reusable Likert row ─────────────────────────────────────────────
function LikertRow({
  label,
  value,
  anchors,
  onChange,
  disabled,
}: {
  label: string
  value: LikertScore | undefined
  anchors: { low: string; high: string }
  onChange: (v: LikertScore) => void
  disabled?: boolean
}) {
  return (
    <Box sx={{ mb: 1.25 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.primary', mb: 0.5 }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: 10, color: 'text.disabled', minWidth: 130 }}>{anchors.low}</Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {[1, 2, 3, 4, 5].map((n) => (
            <Chip
              key={n}
              label={n}
              size="small"
              onClick={() => !disabled && onChange(n as LikertScore)}
              sx={{
                width: 28,
                height: 26,
                fontSize: 11,
                fontWeight: 700,
                bgcolor: value === n ? 'primary.main' : 'grey.100',
                color: value === n ? '#fff' : 'text.primary',
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled && value !== n ? 0.5 : 1,
              }}
            />
          ))}
        </Box>
        <Typography sx={{ fontSize: 10, color: 'text.disabled', minWidth: 130, textAlign: 'left' }}>
          {anchors.high}
        </Typography>
      </Box>
    </Box>
  )
}
